import { useNavigate } from 'react-router-dom';
import {
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
  LEGAL_RESPONSIBLE_PERSON,
  LEGAL_ADDRESS_STREET,
  LEGAL_ADDRESS_CITY,
  LEGAL_ADDRESS_COUNTRY,
  DATA_PROTECTION_AUTHORITY_NAME,
  DATA_PROTECTION_AUTHORITY_ADDRESS,
  DATA_PROTECTION_AUTHORITY_URL,
} from '../constants/contact';

export default function Datenschutz() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Zurück
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-700" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Candlear Booking
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Datenschutzerklärung
          </h1>
          <p className="text-sm text-gray-400 mb-8">Letzte Aktualisierung: Oktober 2026</p>

          {/* Verantwortlicher */}
          <section className="mb-8 p-5 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Verantwortlicher im Sinne der DSGVO</p>
            <p className="text-sm text-gray-800 leading-relaxed">
              <strong>{LEGAL_RESPONSIBLE_PERSON}</strong><br />
              {LEGAL_ADDRESS_STREET}<br />
              {LEGAL_ADDRESS_CITY}<br />
              {LEGAL_ADDRESS_COUNTRY}<br />
              <br />
              E-Mail: <a href="mailto:support@candlear.de" className="text-[#FF6D00] hover:underline">support@candlear.de</a><br />
              Telefon:{' '}
              <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                {SUPPORT_PHONE_DISPLAY}
              </a>
              <br />
              Website: <a href="https://candlear.de" className="text-[#FF6D00] hover:underline">https://candlear.de</a>
            </p>
          </section>

          <LegalSection number="1" title="Überblick und Geltungsbereich">
            <p>
              Diese Datenschutzerklärung gilt für den Besuch der Website <strong>candlear.de</strong> und für die Nutzung von{' '}
              <strong>Candlear Booking</strong> (nachfolgend „die Plattform"), einem cloudbasierten Terminbuchungssystem, das als
              Software-as-a-Service (SaaS) angeboten wird.
            </p>
            <p className="mt-3">
              Die Plattform richtet sich an <strong>Unternehmen</strong> (nachfolgend „Nutzer" oder „Mandanten"), die Candlear Booking
              einsetzen, um Terminbuchungen für ihre eigenen Kunden zu ermöglichen.
            </p>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              <strong>Hinweis für Endkunden der Mandanten:</strong> Wenn Sie einen Termin über das Buchungs-Widget eines Unternehmens
              gebucht haben, das Candlear Booking nutzt, ist das jeweilige Unternehmen der datenschutzrechtlich Verantwortliche für Ihre Daten.
              Synergic Systems verarbeitet diese Daten nur als Auftragsverarbeiter im Auftrag des Unternehmens. Bitte wenden Sie sich mit
              Datenschutzanfragen an das Unternehmen, über dessen Website Sie gebucht haben.
            </div>
          </LegalSection>

          <LegalSection number="2" title="Welche Daten wir verarbeiten">
            <SubSection title="2.1 Besuch der Website und Kontaktaufnahme">
              <p>
                Beim Aufruf von candlear.de verarbeitet unser Hosting-Anbieter Google Firebase Hosting (siehe 4.1) automatisch
                technisch notwendige Daten: <strong>IP-Adresse</strong>, Datum und Uhrzeit, aufgerufene Seite, übertragene Datenmenge,
                Browser und Betriebssystem sowie die zuvor besuchte Seite (Referrer). Ohne diese Daten kann die Website nicht
                ausgeliefert werden.
              </p>
              <p className="mt-3">
                Auf allen Seiten von candlear.de ist außerdem die Fehlerüberwachung <strong>Sentry</strong> eingebunden (siehe 4.5).
                Tritt im Browser ein Fehler auf oder wird eine Stichprobe der Ladezeiten gemessen (rund 10 % der Seitenaufrufe),
                werden IP-Adresse, Browser- und Gerätedaten, die aufgerufene URL und technische Fehlerdetails an Sentry übermittelt.
                Es werden weder Werbe- noch Analyse-Cookies gesetzt, und wir erstellen keine Nutzungsprofile.
              </p>
              <p className="mt-3">
                Wenn Sie uns per E-Mail oder Telefon kontaktieren (z. B. für Fragen, Support oder Anfragen zu Produkten aus dem Shop),
                verarbeiten wir Ihre Angaben (Name, Kontaktdaten, Inhalt der Anfrage), um Ihre Anfrage zu beantworten.
              </p>
              <p className="mt-3 text-sm text-gray-500">
                Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO für Hosting und Fehlerüberwachung (berechtigtes Interesse an einer sicheren
                und funktionsfähigen Website); für Anfragen Art. 6 Abs. 1 lit. b DSGVO, soweit sie einen Vertrag betreffen oder anbahnen,
                im Übrigen Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Beantwortung). Server-Logs werden höchstens 30 Tage
                gespeichert, Anfragen gelöscht, sobald sie erledigt sind und keine Aufbewahrungspflicht besteht.
              </p>
            </SubSection>

            <SubSection title="2.2 Kontodaten (bei Registrierung und Nutzung des Admin-Dashboards)">
              <p>Wenn Sie ein Konto bei Candlear Booking erstellen, verarbeiten wir:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                <li><strong>E-Mail-Adresse</strong> – für Anmeldung und Kommunikation</li>
                <li><strong>Name des Unternehmens</strong> – zur Anzeige im Dashboard und in Bestätigungs-E-Mails</li>
                <li><strong>Kontakt-E-Mail des Unternehmens</strong> – für Benachrichtigungen</li>
                <li><strong>Technische Zugangskennung</strong> (API-Key, Firebase User-ID) – zur Systemidentifikation</li>
              </ul>
              <p className="mt-3 text-sm text-gray-500">
                Die Anmeldung erfolgt wahlweise per E-Mail/Passwort oder über <strong>Google-Login</strong> (Firebase Authentication).
                Bei Google-Login übermitteln wir Ihre Google-Konto-E-Mail-Adresse zur Kontoerstellung an Firebase Authentication.
              </p>
              <p className="mt-3 text-sm text-gray-500">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
            </SubSection>

            <SubSection title="2.3 Konfigurationsdaten">
              <p>Im Rahmen der Einrichtung Ihres Accounts speichern wir:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                <li>Öffnungszeiten, Termintypen und Einstellungen für das Buchungsverhalten</li>
                <li>Ressourcen, z. B. Namen und E-Mail-Adressen Ihrer Mitarbeiter</li>
              </ul>
              <p className="mt-3 text-sm text-gray-500">
                Daten Ihrer Mitarbeiter, die Sie als Ressourcen anlegen, verarbeiten wir – wie die Daten Ihrer Endkunden – ausschließlich
                in Ihrem Auftrag. Verantwortlicher sind insoweit Sie als Mandant; es gilt der{' '}
                <a href="/avv" className="text-[#FF6D00] hover:underline">Auftragsverarbeitungsvertrag (AVV)</a> (siehe 2.5).
                Für Ihre übrigen Einstellungen gilt Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).
              </p>
            </SubSection>

            <SubSection title="2.4 Nutzungsdaten und Logs">
              <p>Bei der Nutzung der Plattform werden automatisch technische Daten verarbeitet:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                <li><strong>IP-Adresse</strong> und Zugriffszeiten (durch Firebase Infrastruktur)</li>
                <li><strong>Fehlerlogs</strong> zur Systemstabilität</li>
                <li><strong>Rate-Limiting-Daten</strong> zum Schutz vor Missbrauch</li>
              </ul>
              <p className="mt-3 text-sm text-gray-500">
                Diese Daten werden für den Betrieb und die Fehlersuche verarbeitet, technische Logs maximal 30 Tage
                (siehe auch <strong>4.5 Sentry</strong> für Fehler- und Performance-Monitoring).
                Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Systemsicherheit).
              </p>
            </SubSection>

            <SubSection title="2.5 Buchungs- und Kundendaten Ihrer Endkunden (Auftragsverarbeitung)">
              <p>Wenn Ihre Kunden über Candlear Booking Termine buchen, verarbeiten wir in Ihrem Auftrag:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                <li>Namen der Endkunden</li>
                <li>E-Mail-Adressen oder Telefonnummern der Endkunden</li>
                <li>Buchungszeitraum und Termintyp</li>
                <li>Chat-Verläufe aus dem Buchungs-Widget</li>
              </ul>
              <p className="mt-3 text-sm text-gray-500">
                Für diese Daten sind <strong>Sie als Mandant</strong> der datenschutzrechtlich Verantwortliche. Synergic Systems handelt als
                Auftragsverarbeiter gemäß Art. 28 DSGVO. Details regelt der{' '}
                <a href="/avv" className="text-[#FF6D00] hover:underline">Auftragsverarbeitungsvertrag (AVV)</a>,
                den Sie bei der Registrierung abschließen.
              </p>
            </SubSection>

            <SubSection title="2.6 Zahlungs- und Abrechnungsdaten (Stripe-Abonnement)">
              <p>
                Wenn Sie ein kostenpflichtiges Abonnement von Candlear Booking abschließen, verarbeiten wir Ihre
                Zahlungsdaten über den Zahlungsdienstleister <strong>Stripe</strong>. Dabei fallen folgende Daten an:
              </p>
              <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                <li><strong>Rechnungsdaten:</strong> Name und E-Mail-Adresse des Kontoinhabers, ggf. Unternehmensname und Rechnungsadresse</li>
                <li><strong>Zahlungsmittelinformationen:</strong> Werden ausschließlich von Stripe verarbeitet und verschlüsselt gespeichert – Synergic Systems erhält keinen Zugriff auf vollständige Kartendaten</li>
                <li><strong>Transaktionsreferenzen:</strong> Stripe Customer ID, Subscription ID, Zahlungsstatus – zur Verwaltung des Abonnements</li>
                <li><strong>Rechnungsdokumente:</strong> Betrag, Datum, Leistungszeitraum (gesetzliche Aufbewahrungspflicht)</li>
              </ul>
              <p className="mt-3 text-sm text-gray-500">
                Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) für die Zahlungsabwicklung;
                Art. 6 Abs. 1 lit. c DSGVO (gesetzliche Verpflichtung) für die steuerrechtliche Aufbewahrung von Rechnungsdaten
                (§ 147 Abs. 3 AO, § 257 Abs. 4 HGB, § 14b UStG – regelmäßig 8 Jahre für Rechnungen und Buchungsbelege).
              </p>
            </SubSection>
          </LegalSection>

          <LegalSection number="3" title="Zwecke der Verarbeitung">
            <p className="mb-4">Wir verarbeiten Ihre Daten zu folgenden Zwecken:</p>
            <LegalTable
              headers={['Zweck', 'Rechtsgrundlage']}
              rows={[
                ['Bereitstellung der Website und Fehlerüberwachung', 'Art. 6 Abs. 1 lit. f DSGVO'],
                ['Beantwortung von Anfragen per E-Mail oder Telefon', 'Art. 6 Abs. 1 lit. b oder lit. f DSGVO'],
                ['Bereitstellung und Betrieb der Plattform', 'Art. 6 Abs. 1 lit. b DSGVO'],
                ['Kundensupport und Fehlerbehebung', 'Art. 6 Abs. 1 lit. b DSGVO'],
                ['Sicherheit und Missbrauchsschutz', 'Art. 6 Abs. 1 lit. f DSGVO'],
                ['Abrechnung und Vertragsmanagement', 'Art. 6 Abs. 1 lit. b DSGVO'],
                ['Verbesserung der Plattform (anonymisiert)', 'Art. 6 Abs. 1 lit. f DSGVO'],
              ]}
            />
            <p className="mt-4">Wir verwenden Ihre Daten <strong>nicht</strong> für Werbezwecke oder den Verkauf an Dritte.</p>
            <p className="mt-3">
              <strong>Pflicht zur Bereitstellung:</strong> Die Angaben bei der Registrierung (E-Mail-Adresse, Unternehmensname) und bei
              Abschluss eines Abonnements (Zahlungs- und Rechnungsdaten) sind für den Vertragsschluss erforderlich. Ohne sie können wir
              kein Konto bzw. Abonnement einrichten. Darüber hinaus sind Sie nicht verpflichtet, uns Daten bereitzustellen.
            </p>
            <p className="mt-3">
              <strong>Keine automatisierte Entscheidungsfindung:</strong> Wir treffen keine ausschließlich automatisierten Entscheidungen
              einschließlich Profiling im Sinne von Art. 22 DSGVO, die Ihnen gegenüber rechtliche Wirkung entfalten oder Sie in ähnlicher
              Weise erheblich beeinträchtigen.
            </p>
          </LegalSection>

          <LegalSection number="4" title="Weitergabe an Dritte und Auftragsverarbeiter">
            <p className="mb-5">
              Wir setzen folgende Dienstleister ein, die Daten in unserem Auftrag verarbeiten (zu Stripe siehe den Hinweis unten).
              Die Rechtsgrundlage der jeweiligen Verarbeitung ergibt sich aus Abschnitt 2 und 3. Unter „Drittlandübermittlung" ist
              angegeben, worauf eine Übermittlung außerhalb der EU bzw. des EWR gestützt wird (Art. 44 ff. DSGVO). Die US-Unternehmen
              Google LLC, Resend, Stripe und Sentry sind nach dem EU-US Data Privacy Framework zertifiziert; für diese besteht ein
              Angemessenheitsbeschluss der EU-Kommission (Art. 45 DSGVO).
            </p>

            <div className="space-y-4">
              {[
                {
                  name: '4.1 Google Firebase / Google Cloud (Google Cloud EMEA Limited bzw. Google Ireland Limited, Irland)',
                  items: [
                    ['Zweck', 'Cloud-Infrastruktur, Datenbankhosting, Authentifizierung, Hosting'],
                    ['Daten', 'Alle gespeicherten Plattformdaten'],
                    ['Datenspeicherort', 'Firestore/Functions: EU – Frankfurt, Deutschland (europe-west3); Hosting/CDN und Authentifizierungsanfragen: global'],
                    ['Vertrag', 'Google Cloud Data Processing Addendum / Firebase Data Processing and Security Terms'],
                    ['Drittlandübermittlung', 'Möglich an Google LLC (USA): EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-Standardvertragsklauseln'],
                    ['Datenschutzinfo', <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer" className="text-[#FF6D00] hover:underline">firebase.google.com/support/privacy</a>],
                  ],
                },
                {
                  name: '4.2 Resend (Resend, Inc., USA)',
                  items: [
                    ['Zweck', 'Versand von Transaktions-E-Mails (Bestätigungscodes, Buchungsbestätigungen)'],
                    ['Daten', 'E-Mail-Adresse und Name des Empfängers, E-Mail-Inhalt'],
                    ['Datenspeicherort', 'USA'],
                    ['Drittlandübermittlung', 'EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-Standardvertragsklauseln; Resend DPA'],
                    ['Datenschutzinfo', <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noreferrer" className="text-[#FF6D00] hover:underline">resend.com/legal/privacy-policy</a>],
                  ],
                },
                {
                  name: '4.3 Google Gemini API (Google Cloud EMEA Limited, Irland)',
                  items: [
                    ['Zweck', 'KI-Buchungsassistenz im Chat-Widget'],
                    ['Daten', 'Chatverlauf, inkl. Name, Kontakt, Termintyp, Wunschzeit (soweit der Endkunde sie eingibt)'],
                    ['Datenspeicherort', 'Global (Gemini Developer API, keine Regionswahl möglich)'],
                    ['Datennutzung', 'Kostenpflichtiger Dienst (Paid Service, Billing aktiv, inkl. Gratis-Kontingent). Google verwendet Prompts und Antworten nicht zur Produktverbesserung. Zeitlich begrenzte Abuse-Logs und Caching können in jedem Land mit Google-Infrastruktur liegen.'],
                    ['Vertrag', 'Gemini API Terms, Data Processing Addendum for Products Where Google is a Data Processor'],
                    ['Drittlandübermittlung', 'An Google LLC (USA) und weltweite Google-Infrastruktur: EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-Standardvertragsklauseln'],
                    ['Datenschutzinfo', <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noreferrer" className="text-[#FF6D00] hover:underline">ai.google.dev/gemini-api/terms</a>],
                  ],
                },
                {
                  name: '4.4 Stripe (Stripe Payments Europe, Limited, Dublin, Irland)',
                  items: [
                    ['Zweck', 'Zahlungsabwicklung für kostenpflichtige Candlear Booking Abonnements'],
                    ['Daten', 'Name, E-Mail-Adresse, Rechnungsadresse des Mandanten; Zahlungsmittelinformationen (werden von Stripe direkt und verschlüsselt verarbeitet); Transaktionsreferenzen (Subscription ID, Payment Status)'],
                    ['Datenspeicherort', 'EU; Übermittlung an Stripe, Inc. (USA) möglich'],
                    ['Rechtsgrundlage', 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)'],
                    ['Drittlandübermittlung', 'EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-Standardvertragsklauseln; Stripe DPA'],
                    ['Hinweis', 'Stripe ist nach PCI DSS Level 1 zertifiziert. Synergic Systems hat keinen Zugriff auf vollständige Zahlungsmitteldaten.'],
                    ['Datenschutzinfo', <a href="https://stripe.com/de/privacy" target="_blank" rel="noreferrer" className="text-[#FF6D00] hover:underline">stripe.com/de/privacy</a>],
                  ],
                },
                {
                  name: '4.5 Sentry (Functional Software, Inc., USA)',
                  items: [
                    ['Zweck', 'Fehler- und Performance-Überwachung (Backend sowie alle Seiten von candlear.de im Browser, einschließlich Admin-Dashboard)'],
                    ['Daten', 'IP-Adresse, Browser- und Gerätedaten, aufgerufene URL, technische Fehlerberichte (Stacktrace), Ladezeiten-Stichprobe; keine Zahlungsdaten und keine Inhalte von Anfragen (z. B. Chat-Nachrichten)'],
                    ['Datenspeicherort', 'USA'],
                    ['Rechtsgrundlage', 'Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer stabilen, fehlerfreien Plattform)'],
                    ['Drittlandübermittlung', 'EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-Standardvertragsklauseln; Sentry DPA'],
                    ['Datenschutzinfo', <a href="https://sentry.io/privacy/" target="_blank" rel="noreferrer" className="text-[#FF6D00] hover:underline">sentry.io/privacy</a>],
                  ],
                },
              ].map(provider => (
                <div key={provider.name} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                    <p className="text-sm font-semibold text-gray-700">{provider.name}</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {provider.items.map(([label, value]) => (
                      <div key={String(label)} className="flex px-4 py-2.5 text-sm">
                        <span className="w-36 flex-shrink-0 font-medium text-gray-500">{label}</span>
                        <span className="text-gray-700">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <strong>Hinweis:</strong> Wenn Sie das KI-gestützte Chat-Widget einsetzen, werden Chat-Inhalte Ihrer Endkunden an
              Google Gemini übertragen. Bitte informieren Sie Ihre Endkunden in Ihrer eigenen Datenschutzerklärung darüber.
            </div>

            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              <strong>Hinweis zur Zahlungsabwicklung:</strong> Stripe agiert im Rahmen der Zahlungsabwicklung als eigenständig
              Verantwortlicher für gesetzlich vorgeschriebene Zwecke (z. B. Betrugsprävention, Compliance). Weitere Informationen
              entnehmen Sie der <a href="https://stripe.com/de/privacy" target="_blank" rel="noreferrer" className="underline">Datenschutzerklärung von Stripe</a>.
            </div>

            <p className="mt-4 text-sm text-gray-600">
              Wir geben Ihre Daten ausschließlich an die in diesem Abschnitt 4 genannten Auftragsverarbeiter weiter,
              sowie zusätzlich, wenn wir gesetzlich dazu verpflichtet sind.
            </p>
          </LegalSection>

          <LegalSection number="5" title="Datenspeicherung und Aufbewahrungsfristen">
            <LegalTable
              headers={['Datenkategorie', 'Speicherdauer']}
              rows={[
                ['Kontodaten (E-Mail, Firmenname)', 'Bis zur Kontolöschung – bei Selbstlöschung im Dashboard unmittelbar, sonst spätestens 30 Tage nach Vertragsende'],
                ['Buchungsdaten und Kundendaten', 'Bis zur Kontolöschung (im Auftrag des Mandanten)'],
                ['Temporäre Buchungscodes und Anmeldecodes', '10 Minuten nach Erstellung'],
                ['Benachrichtigungen im Dashboard (Kundenname, Terminzeit)', '90 Tage'],
                ['Chat-Sessions', '24 Stunden nach der letzten Interaktion'],
                ['Technische Logs', 'Maximal 30 Tage'],
                ['Abrechnungs- und Zahlungsdaten (Rechnungen, Buchungsbelege)', '8 Jahre (gesetzliche Aufbewahrungspflicht gem. § 147 Abs. 3 AO, § 257 Abs. 4 HGB, § 14b UStG)'],
                ['Stripe Customer ID / Subscription ID', 'Bis zur Kontolöschung; Rechnungsreferenzen 8 Jahre'],
              ]}
            />
            <p className="mt-4 text-sm text-gray-600">
              Nach Ablauf der Speicherfrist werden Daten automatisch oder auf Anfrage gelöscht.
            </p>
          </LegalSection>

          <LegalSection number="6" title="Datenspeicherort und Datensicherheit">
            <p>
              Kernspeicherung (Firestore, Cloud Functions, Kontodaten) erfolgt im Google Cloud-Rechenzentrum
              <strong> Frankfurt, Deutschland</strong> (Region: europe-west3).
            </p>
            <p className="mt-3">
              Nicht alle Verarbeitungen finden ausschließlich in der EU statt: Firebase Hosting wird über ein globales CDN
              ausgeliefert (Zugriffs-IPs können an Edge-Standorten außerhalb der EU verarbeitet werden), und einzelne
              Auftragsverarbeiter sitzen außerhalb der EU. Details und die jeweilige Rechtsgrundlage für Drittlandtransfers
              finden Sie in Abschnitt 4.
            </p>
            <p className="mt-3">Wir setzen folgende technische und organisatorische Maßnahmen (TOMs) ein:</p>
            <ul className="mt-2 space-y-1.5 list-disc list-inside text-gray-700">
              <li><strong>Transportverschlüsselung:</strong> HTTPS/TLS für alle Verbindungen</li>
              <li><strong>Ruhende Datenverschlüsselung:</strong> Firestore verschlüsselt alle Daten at rest</li>
              <li><strong>Zugriffskontrolle:</strong> Firebase Security Rules – Mandanten haben ausschließlich Zugriff auf eigene Daten</li>
              <li><strong>Authentifizierung:</strong> Firebase Authentication mit sicherem Token-Management</li>
              <li><strong>Geheimnismanagement:</strong> API-Keys und Credentials in Google Secret Manager, nie im Code</li>
            </ul>
          </LegalSection>

          <LegalSection number="7" title="Ihre Rechte als betroffene Person">
            <p className="mb-4">Als betroffene Person haben Sie folgende Rechte:</p>
            <LegalTable
              headers={['Recht', 'Inhalt', 'Wie geltend machen']}
              rows={[
                [
                  'Auskunft (Art. 15)',
                  'Welche Daten wir über Sie speichern',
                  <>
                    E-Mail an support@candlear.de oder telefonisch unter{' '}
                    <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                      {SUPPORT_PHONE_DISPLAY}
                    </a>
                  </>,
                ],
                ['Berichtigung (Art. 16)', 'Korrektur unrichtiger Daten', 'Im Admin-Dashboard oder per E-Mail'],
                ['Löschung (Art. 17)', 'Löschung Ihres Accounts und aller Daten', 'Einstellungen → Datenschutz, oder per E-Mail'],
                ['Einschränkung (Art. 18)', 'Eingeschränkte Verarbeitung', 'Per E-Mail'],
                ['Datenübertragbarkeit (Art. 20)', 'Export Ihrer Daten (JSON)', 'Im Admin-Dashboard: Einstellungen → Datenschutz, oder per E-Mail'],
                ['Widerspruch (Art. 21)', 'Widerspruch gegen Verarbeitung auf Basis berechtigter Interessen', 'Per E-Mail'],
              ]}
            />
            <p className="mt-4 text-sm text-gray-600">
              Wir beantworten Anfragen unverzüglich, spätestens innerhalb <strong>eines Monats</strong> (Art. 12 Abs. 3 DSGVO).
            </p>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
              <p className="font-semibold mb-1">Widerspruchsrecht nach Art. 21 DSGVO</p>
              <p>
                Soweit wir Ihre Daten auf Grundlage berechtigter Interessen (Art. 6 Abs. 1 lit. f DSGVO) verarbeiten, haben Sie das Recht,
                aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit Widerspruch gegen diese Verarbeitung einzulegen.
                Wir verarbeiten die Daten dann nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe nachweisen, die Ihre
                Interessen, Rechte und Freiheiten überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung
                von Rechtsansprüchen. Der Widerspruch ist formlos möglich, am besten per E-Mail an{' '}
                <a href="mailto:datenschutz@candlear.de" className="underline">datenschutz@candlear.de</a>.
              </p>
            </div>
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">
              <p className="font-medium mb-1">Beschwerderecht bei der Aufsichtsbehörde:</p>
              <p>
                {DATA_PROTECTION_AUTHORITY_NAME}<br />
                {DATA_PROTECTION_AUTHORITY_ADDRESS}<br />
                <a href={DATA_PROTECTION_AUTHORITY_URL} target="_blank" rel="noreferrer" className="text-[#FF6D00] hover:underline">{DATA_PROTECTION_AUTHORITY_URL.replace('https://', '')}</a>
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Sie können sich stattdessen auch an jede andere Datenschutzaufsichtsbehörde in der EU wenden (Art. 77 DSGVO).
              </p>
            </div>
          </LegalSection>

          <LegalSection number="8" title="Cookies und lokale Datenspeicherung">
            <SubSection title="Admin-Dashboard">
              <p>
                Das Admin-Dashboard setzt technisch notwendige Sitzungsdaten für die Authentifizierung (Firebase Authentication) und speichert im localStorage den Zeitpunkt des zuletzt angeforderten Anmeldecodes, damit nicht mehrfach Codes verschickt werden.
                Diese sind für den Betrieb unbedingt erforderlich und erfordern keine Einwilligung.
              </p>
            </SubSection>
            <SubSection title="Buchungs-Widget (für Endkunden der Mandanten)">
              <p>
                Das eingebettete Chat-Widget speichert den Chat-Verlauf im <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">sessionStorage</code> des Browsers.
                Diese Daten werden <strong>ausschließlich lokal</strong> im Browser des Nutzers gespeichert und beim Schließen
                des Browser-Tabs automatisch gelöscht. Es werden keine persistenten Cookies gesetzt.
              </p>
            </SubSection>
          </LegalSection>

          <LegalSection number="9" title="Minderjährige">
            <p>
              Der Betriebs-Account der Plattform richtet sich ausschließlich an Unternehmer (§ 14 BGB);
              Kontoinhaber müssen volljährig sein. Wir verarbeiten wissentlich keine Kontodaten von Minderjährigen.
            </p>
            <p className="mt-3">
              Buchungsdaten, die Endkunden über das Widget eines Mandanten eingeben, verarbeiten wir ausschließlich
              als Auftragsverarbeiter. Buchen dort auch Minderjährige Termine, ist der jeweilige Mandant als
              Verantwortlicher für die Rechtsgrundlage – einschließlich Art. 8 DSGVO – zuständig. Eine Altersprüfung
              der Endkunden findet technisch nicht statt.
            </p>
          </LegalSection>

          <LegalSection number="10" title="Änderungen dieser Datenschutzerklärung">
            <p>
              Wir behalten uns vor, diese Datenschutzerklärung bei Änderungen der Plattform oder der Rechtslage anzupassen.
              Die aktuelle Version ist stets unter{' '}
              <a href="https://candlear.de/datenschutz" className="text-[#FF6D00] hover:underline">candlear.de/datenschutz</a> abrufbar.
              Bei wesentlichen Änderungen informieren wir registrierte Nutzer per E-Mail.
            </p>
          </LegalSection>

          <LegalSection number="11" title="Kontakt für Datenschutzanfragen">
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">
              <strong>{LEGAL_RESPONSIBLE_PERSON}</strong><br />
              E-Mail: <a href="mailto:datenschutz@candlear.de" className="text-[#FF6D00] hover:underline">datenschutz@candlear.de</a><br />
              {LEGAL_ADDRESS_STREET}<br />
              {LEGAL_ADDRESS_CITY}
            </div>
          </LegalSection>

        </div>
      </div>
    </div>
  );
}

function LegalSection({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200 flex items-baseline gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <span className="text-[#FF6D00] font-mono text-sm">{number}.</span>
        {title}
      </h2>
      <div className="text-sm text-gray-700 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <h3 className="text-sm font-semibold text-gray-800 mb-2">{title}</h3>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
}

function LegalTable({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {headers.map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50 transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
